import React, { useState, useEffect, useContext, useMemo } from 'react';

import { apiClient, getApiErrorMessage, getMessageTemplates, getContacts, getGroups } from '../../api-wa';
import { ToastContext } from '../../Context/WaToastContext';


// Only QUICK_REPLY (and FLOW) template buttons ever generate a reply webhook from WhatsApp.
// URL / PHONE_NUMBER / COPY_CODE buttons just open a link or dial a number on-device and never
// notify us, so they're excluded here rather than shown as if they were configurable triggers.
const REPLYABLE_BUTTON_TYPES = new Set(['QUICK_REPLY', 'FLOW']);

// Mirrors the categorization /reminders/analytics/ computes server-side from button_response +
// AutomationLog, so "audience from campaign replies" always matches what the Analytics tab shows.
const REPLY_CATEGORIES = [
  { value: 'INTERESTED', label: '🟢 Interested / Attending' },
  { value: 'LATER_DATES', label: '📅 Interested for Later Dates' },
  { value: 'NOT_INTERESTED', label: '🔴 Not Interested' },
  { value: 'NO_RESPONSE', label: '⏳ No Response Yet' },
];

const getTemplateReplyButtons = (tpl) => {
  if (!tpl || !Array.isArray(tpl.buttons)) return [];
  return tpl.buttons
    .filter(b => {
      if (typeof b === 'string') return true; // legacy stored format with no type info
      const btnType = String(b.type || '').toUpperCase();
      return !btnType || REPLYABLE_BUTTON_TYPES.has(btnType);
    })
    .map(b => (typeof b === 'string' ? b : b.text))
    .filter(Boolean);
};

// Recursively renders "if contact clicks this button, do X" rules for any template's buttons.
// When the configured action is SEND_TEMPLATE, it looks up that template's own reply buttons
// (dynamically, from whatever is actually synced from Meta) and renders another nested editor
// for them - so a workflow can chain arbitrarily deep (button -> template -> its buttons -> ...)
// without any button text, template name, or event date ever being hardcoded.
const ButtonActionsEditor = ({ buttons, actions, onChange, templates, followUpWorkflows, campaignForm, depth = 0 }) => {
  const currentActions = actions || {};

  const updateBtn = (btnName, field, value) => {
    const next = { ...currentActions };
    const cfg = next[btnName] || { action: 'CONTINUE', reply_text: '' };
    next[btnName] = { ...cfg, [field]: value };
    onChange(next);
  };

  const updateBtnVar = (btnName, varIdx, value) => {
    const next = { ...currentActions };
    const cfg = next[btnName] || { action: 'SEND_TEMPLATE', template_variables: { body_variables: [] } };
    const tplVars = { ...(cfg.template_variables || { body_variables: [] }) };
    const bodyVars = [...(tplVars.body_variables || [])];
    bodyVars[varIdx] = value;
    tplVars.body_variables = bodyVars;
    next[btnName] = { ...cfg, template_variables: tplVars };
    onChange(next);
  };

  const removeBtn = (btnName) => {
    const next = { ...currentActions };
    delete next[btnName];
    onChange(next);
  };

  const addCustomBtn = () => {
    const btnName = prompt('Enter template button text (e.g. Confirm, Decline, Reschedule):');
    if (!btnName || !btnName.trim()) return;
    updateBtn(btnName.trim(), 'action', 'CONTINUE');
  };

  const allBtnNames = Array.from(new Set([...(buttons || []), ...Object.keys(currentActions)]));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
      {depth === 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={addCustomBtn}
            style={{ background: 'rgba(124, 77, 255, 0.1)', color: 'var(--brand-purple)', border: 'none', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
          >
            + Add Button Action
          </button>
        </div>
      )}

      {allBtnNames.length === 0 ? (
        <div style={{ fontSize: '12px', color: '#94a3b8', fontStyle: 'italic' }}>
          No interactive template buttons detected. Select a template with buttons or click "+ Add Button Action" to configure follow-up responses!
        </div>
      ) : (
        allBtnNames.map(btnName => {
          const actionCfg = currentActions[btnName] || { action: 'CONTINUE', reply_text: '' };
          const triggeredTpl = actionCfg.action === 'SEND_TEMPLATE'
            ? templates.find(t => String(t.id) === String(actionCfg.template_id))
            : null;

          let tplVarCount = 0;
          if (triggeredTpl) {
            if (triggeredTpl.variables_count) tplVarCount = triggeredTpl.variables_count;
            else if (triggeredTpl.body) {
              const m = triggeredTpl.body.match(/\{\{([^}]+)\}\}/g);
              if (m) tplVarCount = m.length;
            }
          }

          const nestedButtons = triggeredTpl ? getTemplateReplyButtons(triggeredTpl) : [];
          const hasNestedConfig = Object.keys(actionCfg.next_button_actions || {}).length > 0;

          return (
            <div key={btnName} style={{ background: 'var(--surface-white)', borderRadius: '10px', padding: '12px', border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--brand-purple)' }}>Button Reply: "{btnName}"</span>
                <button
                  type="button"
                  onClick={() => removeBtn(btnName)}
                  style={{ background: 'transparent', border: 'none', color: '#f87171', fontSize: '11px', cursor: 'pointer', fontWeight: '600' }}
                >
                  Remove
                </button>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: '10px', alignItems: 'center' }}>
                <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>Action On Click</label>
                <select
                  value={actionCfg.action || 'CONTINUE'}
                  onChange={e => updateBtn(btnName, 'action', e.target.value)}
                  style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border-color)', fontSize: '12px', outline: 'none', background: 'var(--surface-white)' }}
                >
                  <option value="CONTINUE">🟢 Continue Reminders Sequence</option>
                  <option value="STOP">🔴 Stop / Divert Reminders for Contact</option>
                  <option value="SEND_TEMPLATE">📩 Send Template Message</option>
                  <option value="TRIGGER_FOLLOWUP">🔁 Trigger Follow-up Workflow</option>
                  <option value="FOLLOWUP_REPLY">💬 Send Custom Text Reply</option>
                </select>
              </div>

              {actionCfg.action === 'SEND_TEMPLATE' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '4px', background: 'var(--surface-soft-2)', padding: '10px', borderRadius: '8px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>Select Template to Send On Click</label>
                  <select
                    value={actionCfg.template_id || ''}
                    onChange={e => updateBtn(btnName, 'template_id', e.target.value)}
                    style={{ padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', fontSize: '12px', outline: 'none', background: 'var(--surface-white)' }}
                  >
                    <option value="">-- Select Template --</option>
                    {templates.map(t => (
                      <option key={t.id} value={t.id}>{t.name}</option>
                    ))}
                  </select>

                  {triggeredTpl && (
                    <div style={{ background: 'var(--surface-white)', padding: '8px 10px', borderRadius: '6px', border: '1px solid var(--border-color)', fontSize: '11px', color: '#475569' }}>
                      <strong>Template Text Preview:</strong> "{triggeredTpl.body}"
                    </div>
                  )}

                  {tplVarCount > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginTop: '4px' }}>
                      <label style={{ fontSize: '11px', fontWeight: '700', color: 'var(--brand-purple)' }}>
                        Triggered Template Variables ({tplVarCount} parameter{tplVarCount > 1 ? 's' : ''})
                      </label>
                      {Array.from({ length: tplVarCount }).map((_, vIdx) => {
                        const currentVarVals = (actionCfg.template_variables || {}).body_variables || [];
                        return (
                          <div key={vIdx} style={{ display: 'flex', flexDirection: 'column', gap: '4px', background: 'var(--surface-white)', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '4px' }}>
                              <span style={{ fontSize: '11px', fontWeight: '700', color: '#475569' }}>Variable &#123;&#123;{vIdx + 1}&#125;&#125;</span>
                              <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                                <button type="button" onClick={() => updateBtnVar(btnName, vIdx, '{{contact.name}}')} style={{ fontSize: '10px', background: 'rgba(124, 77, 255, 0.1)', color: 'var(--brand-purple)', border: 'none', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', fontWeight: '600' }}>+ Contact Name</button>
                                <button type="button" onClick={() => updateBtnVar(btnName, vIdx, campaignForm.event_title || '{{event.title}}')} style={{ fontSize: '10px', background: 'rgba(124, 77, 255, 0.1)', color: 'var(--brand-purple)', border: 'none', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', fontWeight: '600' }}>+ Event Title</button>
                                <button type="button" onClick={() => updateBtnVar(btnName, vIdx, campaignForm.event_date_time || '{{event.date}}')} style={{ fontSize: '10px', background: 'rgba(124, 77, 255, 0.1)', color: 'var(--brand-purple)', border: 'none', borderRadius: '4px', padding: '2px 6px', cursor: 'pointer', fontWeight: '600' }}>+ Event Date</button>
                              </div>
                            </div>
                            <input
                              type="text"
                              placeholder={`Value for {{${vIdx + 1}}} (e.g. {{contact.name}} or 15 August 2026)`}
                              value={currentVarVals[vIdx] || ''}
                              onChange={e => updateBtnVar(btnName, vIdx, e.target.value)}
                              style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid var(--border-color)', fontSize: '12px', outline: 'none', background: 'var(--surface-soft-2)' }}
                            />
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {triggeredTpl && (nestedButtons.length > 0 || hasNestedConfig) && (
                    <div style={{ marginTop: '6px', paddingLeft: '12px', borderLeft: '2px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ fontSize: '11px', fontWeight: '700', color: 'var(--brand-purple)' }}>
                        ↳ When "{triggeredTpl.name}" is delivered, handle its button replies:
                      </div>
                      <ButtonActionsEditor
                        buttons={nestedButtons}
                        actions={actionCfg.next_button_actions || {}}
                        onChange={(nextActions) => updateBtn(btnName, 'next_button_actions', nextActions)}
                        templates={templates}
                        followUpWorkflows={followUpWorkflows}
                        campaignForm={campaignForm}
                        depth={depth + 1}
                      />
                    </div>
                  )}
                </div>
              )}

              {actionCfg.action === 'TRIGGER_FOLLOWUP' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>Select Follow-up Workflow Sequence</label>
                  <select
                    value={actionCfg.followup_workflow_id || ''}
                    onChange={e => updateBtn(btnName, 'followup_workflow_id', e.target.value)}
                    style={{ padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', fontSize: '12px', outline: 'none', background: 'var(--surface-white)' }}
                  >
                    <option value="">-- Select Follow-up Workflow --</option>
                    {followUpWorkflows.map(wf => (
                      <option key={wf.id} value={wf.id}>
                        {wf.name} ({wf.steps?.length || 0} step{wf.steps?.length === 1 ? '' : 's'})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {actionCfg.action === 'FOLLOWUP_REPLY' && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginTop: '4px' }}>
                  <label style={{ fontSize: '11px', fontWeight: '600', color: '#64748b' }}>Automated Reply Message (Alternate Dates / Follow-up Text)</label>
                  <textarea
                    rows="2"
                    placeholder="e.g. Thanks for replying! We have upcoming session dates - reply with your preferred date to reserve your slot!"
                    value={actionCfg.reply_text || ''}
                    onChange={e => updateBtn(btnName, 'reply_text', e.target.value)}
                    style={{ padding: '8px', borderRadius: '6px', border: '1px solid var(--border-color)', fontSize: '12px', outline: 'none', background: 'var(--surface-white)' }}
                  />
                </div>
              )}
            </div>
          );
        })
      )}
    </div>
  );
};

const ReminderAutomation = () => {
  const { addToast } = useContext(ToastContext);
  const [activeTab, setActiveTab] = useState('campaigns'); // 'campaigns' | 'history'
  const [campaigns, setCampaigns] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [contactSortOrder, setContactSortOrder] = useState('latest');
  const [contactDateFrom, setContactDateFrom] = useState('');
  const [contactDateTo, setContactDateTo] = useState('');
  const [contactSearchQuery, setContactSearchQuery] = useState('');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // Analytics State
  const [analytics, setAnalytics] = useState({
    total_scheduled: 0,
    total_sent: 0,
    total_failed: 0,
    delivery_rate: 95.5,
    read_rate: 78.2,
    attendance_rate: 85.0
  });

  // Modal / Form State
  const [showModal, setShowModal] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Asia/Kolkata';

  // Audience source: build a new campaign's audience either by picking contacts manually,
  // by targeting contacts based on how they replied to an existing campaign's buttons
  // (Interested / Interested for Later Dates / Not Interested / No Response Yet), or by
  // picking an existing Contacts-page group.
  const [audienceMode, setAudienceMode] = useState('manual'); // 'manual' | 'campaign_replies' | 'contact_group'
  const [sourceCampaignId, setSourceCampaignId] = useState('');
  const [sourceCategory, setSourceCategory] = useState('NOT_INTERESTED');
  const [sourceContacts, setSourceContacts] = useState([]);
  const [loadingSourceContacts, setLoadingSourceContacts] = useState(false);
  const [contactGroups, setContactGroups] = useState([]);
  const [sourceGroupId, setSourceGroupId] = useState('');

  const [campaignForm, setCampaignForm] = useState({
    name: '',
    event_type: 'WEBINAR',
    event_title: '',
    event_date_time: '',
    time_zone: userTimeZone,
    is_active: true,
    contacts: [],
    schedules: []
  });

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/reminders/');
      setCampaigns(response.data?.results ?? response.data ?? []);
    } catch (err) {
      console.error(err);
      addToast(getApiErrorMessage(err, 'Failed to fetch campaigns'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const data = await getMessageTemplates();
      setTemplates(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAllContacts = async () => {
    try {
      // Backend caps page_size at 10000 (ContactPagination.max_page_size); request the
      // max explicitly so "Select All" covers every contact, not just the default first page.
      const data = await getContacts({ page_size: 10000 });
      setContacts(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchContactGroups = async () => {
    try {
      const data = await getGroups();
      setContactGroups(data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await apiClient.get('/reminders/history/');
      setLogs(response.data?.results ?? response.data ?? []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const response = await apiClient.get('/reminders/analytics/');
      setAnalytics(response.data);
    } catch (err) {
      console.error(err);
    }
  };

  const [followUpWorkflows, setFollowUpWorkflows] = useState([]);

  const fetchFollowUpWorkflows = async () => {
    try {
      const response = await apiClient.get('/follow-ups/');
      setFollowUpWorkflows(response.data?.results ?? response.data ?? []);
    } catch (err) {
      console.error('Error fetching follow-up workflows:', err);
    }
  };

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
    fetchAllContacts();
    fetchAnalytics();
    fetchFollowUpWorkflows();
    fetchContactGroups();
  }, []);

  useEffect(() => {
    if (activeTab === 'history') {
      fetchHistory();
    }
  }, [activeTab]);

  const handleOpenCreate = () => {
    setEditingCampaign(null);
    setAudienceMode('manual');
    setSourceCampaignId('');
    setSourceCategory('NOT_INTERESTED');
    setSourceContacts([]);
    setSourceGroupId('');
    setContactSearchQuery('');
    setCampaignForm({
      name: '',
      event_type: 'WEBINAR',
      event_title: '',
      event_date_time: '',
      time_zone: 'UTC',
      is_active: true,
      contacts: [],
      schedules: [
        { timing_type: '1_DAY_BEFORE', custom_minutes_before: '', template: '', media_url: '' }
      ]
    });
    setShowModal(true);
  };

  const handleOpenEdit = (camp) => {
    setEditingCampaign(camp);
    setAudienceMode('manual');
    setSourceCampaignId('');
    setSourceCategory('NOT_INTERESTED');
    setSourceContacts([]);
    setSourceGroupId('');
    setContactSearchQuery('');

    // Format date-time for input type="datetime-local"
    let formattedDate = '';
    if (camp.event_date_time) {
      const d = new Date(camp.event_date_time);
      const pad = (num) => String(num).padStart(2, '0');
      formattedDate = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
    }

    setCampaignForm({
      name: camp.name,
      event_type: camp.event_type,
      event_title: camp.event_title,
      event_date_time: formattedDate,
      time_zone: camp.time_zone || 'UTC',
      is_active: camp.is_active,
      is_interconnected: camp.is_interconnected || false,
      alternate_dates: camp.alternate_dates || [],
      contacts: camp.contacts || [],
      schedules: (camp.schedules || []).map(s => ({
        id: s.id,
        timing_type: s.timing_type,
        custom_minutes_before: s.custom_minutes_before || '',
        template: s.template,
        media_url: s.media_url || '',
        template_variables: s.template_variables || {},
        button_actions: s.button_actions || {}
      }))
    });
    setShowModal(true);
  };

  const handleScheduleVariablesChange = (scheduleIdx, field, value) => {
    const updatedSchedules = [...campaignForm.schedules];
    const currentVars = updatedSchedules[scheduleIdx].template_variables || { body_variables: [], header_text: '', media_url: '' };
    updatedSchedules[scheduleIdx].template_variables = {
      ...currentVars,
      [field]: value
    };
    setCampaignForm({ ...campaignForm, schedules: updatedSchedules });
  };

  const handleScheduleBodyVariableChange = (scheduleIdx, varIdx, value) => {
    const updatedSchedules = [...campaignForm.schedules];
    const currentVars = updatedSchedules[scheduleIdx].template_variables || { body_variables: [], header_text: '', media_url: '' };
    const bodyVars = [...(currentVars.body_variables || [])];
    bodyVars[varIdx] = value;
    updatedSchedules[scheduleIdx].template_variables = {
      ...currentVars,
      body_variables: bodyVars
    };
    setCampaignForm({ ...campaignForm, schedules: updatedSchedules });
  };

  const handleRemoveScheduleBodyVariable = (scheduleIdx, varIdx) => {
    const updatedSchedules = [...campaignForm.schedules];
    const currentVars = updatedSchedules[scheduleIdx].template_variables || { body_variables: [], header_text: '', media_url: '' };
    const bodyVars = [...(currentVars.body_variables || [])];
    bodyVars.splice(varIdx, 1);
    updatedSchedules[scheduleIdx].template_variables = {
      ...currentVars,
      body_variables: bodyVars
    };
    setCampaignForm({ ...campaignForm, schedules: updatedSchedules });
  };

  const handleInsertVariableShortcut = (scheduleIdx, varIdx, value) => {
    handleScheduleBodyVariableChange(scheduleIdx, varIdx, value);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!campaignForm.name.trim() || !campaignForm.event_title.trim() || !campaignForm.event_date_time) {
      addToast('Please fill out all required fields', 'warning');
      return;
    }

    if (campaignForm.contacts.length === 0) {
      addToast('Please select at least one contact', 'warning');
      return;
    }

    if (campaignForm.schedules.some(s => !s.template)) {
      addToast('Please select a template for all schedules', 'warning');
      return;
    }

    const cleanAltDates = (campaignForm.alternate_dates || []).filter(d => String(d).trim());

    const payloadSchedules = campaignForm.schedules.map(s => ({
      id: s.id,
      timing_type: s.timing_type,
      custom_minutes_before: s.timing_type === 'CUSTOM' ? (parseInt(s.custom_minutes_before, 10) || 0) : null,
      template: typeof s.template === 'object' && s.template !== null ? s.template.id : s.template,
      media_url: s.media_url || (s.template_variables || {}).media_url || null,
      template_variables: s.template_variables || {},
      button_actions: s.button_actions || {}
    }));

    const payload = {
      name: campaignForm.name,
      event_type: campaignForm.event_type,
      event_title: campaignForm.event_title,
      event_date_time: new Date(campaignForm.event_date_time).toISOString(),
      time_zone: campaignForm.time_zone,
      is_active: campaignForm.is_active,
      is_interconnected: campaignForm.is_interconnected || false,
      alternate_dates: cleanAltDates,
      contacts: campaignForm.contacts,
      schedules: payloadSchedules
    };

    try {
      setSaving(true);
      if (editingCampaign) {
        await apiClient.patch(`/reminders/${editingCampaign.id}/`, payload);
        addToast('Campaign updated successfully', 'success');
      } else {
        await apiClient.post('/reminders/', payload);
        addToast('Campaign created successfully', 'success');
      }
      setShowModal(false);
      fetchCampaigns();
      fetchAnalytics();
    } catch (err) {
      console.error(err);
      addToast(getApiErrorMessage(err, 'Failed to save campaign'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (camp) => {
    try {
      const updated = await apiClient.patch(`/reminders/${camp.id}/`, { is_active: !camp.is_active });
      setCampaigns(campaigns.map(c => c.id === camp.id ? updated.data : c));
      addToast(`Campaign ${!camp.is_active ? 'enabled' : 'disabled'} successfully`, 'success');
      fetchAnalytics();
    } catch (err) {
      console.error(err);
      addToast(getApiErrorMessage(err, 'Failed to toggle status'), 'error');
    }
  };

  const handleDuplicate = async (camp) => {
    try {
      await apiClient.post(`/reminders/${camp.id}/duplicate/`);
      addToast('Campaign duplicated successfully', 'success');
      fetchCampaigns();
      fetchAnalytics();
    } catch (err) {
      console.error(err);
      addToast(getApiErrorMessage(err, 'Failed to duplicate campaign'), 'error');
    }
  };

  // Fetches how contacts replied to a source campaign's buttons and narrows the list to one
  // reply category, reusing the exact categorization /reminders/analytics/ already computes.
  const fetchSourceContacts = async (campaignId, category) => {
    if (!campaignId) {
      setSourceContacts([]);
      return;
    }
    try {
      setLoadingSourceContacts(true);
      const response = await apiClient.get('/reminders/analytics/', { params: { campaign_id: campaignId } });
      const breakdown = response.data?.contacts || [];
      const filtered = breakdown.filter((row) => row.category === category);
      setSourceContacts(filtered);
      setCampaignForm((prev) => ({ ...prev, contacts: filtered.map((row) => row.contact_id) }));
    } catch (err) {
      console.error(err);
      addToast(getApiErrorMessage(err, 'Failed to load contacts for the selected campaign'), 'error');
      setSourceContacts([]);
    } finally {
      setLoadingSourceContacts(false);
    }
  };

  useEffect(() => {
    if (audienceMode === 'campaign_replies' && sourceCampaignId) {
      fetchSourceContacts(sourceCampaignId, sourceCategory);
    }
  }, [audienceMode, sourceCampaignId, sourceCategory]);

  const handleSelectAllSourceContacts = () => {
    const visibleIds = sourceContacts.map((row) => row.contact_id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => campaignForm.contacts.includes(id));
    if (allVisibleSelected) {
      setCampaignForm({ ...campaignForm, contacts: campaignForm.contacts.filter((id) => !visibleIds.includes(id)) });
    } else {
      setCampaignForm({ ...campaignForm, contacts: Array.from(new Set([...campaignForm.contacts, ...visibleIds])) });
    }
  };

  // Members of the selected Contacts-page group, derived from the already-loaded full contact
  // list (each contact carries its group memberships) rather than a separate API call.
  const groupContacts = useMemo(() => {
    if (!sourceGroupId) return [];
    return contacts.filter((c) => (c.groups || []).some((g) => String(g.id) === String(sourceGroupId)));
  }, [contacts, sourceGroupId]);

  const handleSourceGroupChange = (groupId) => {
    setSourceGroupId(groupId);
    const ids = contacts
      .filter((c) => (c.groups || []).some((g) => String(g.id) === String(groupId)))
      .map((c) => c.id);
    setCampaignForm((prev) => ({ ...prev, contacts: ids }));
  };

  const handleSelectAllGroupContacts = () => {
    const visibleIds = groupContacts.map((c) => c.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => campaignForm.contacts.includes(id));
    if (allVisibleSelected) {
      setCampaignForm({ ...campaignForm, contacts: campaignForm.contacts.filter((id) => !visibleIds.includes(id)) });
    } else {
      setCampaignForm({ ...campaignForm, contacts: Array.from(new Set([...campaignForm.contacts, ...visibleIds])) });
    }
  };

  // Quick-launch: open Create Campaign pre-set to target Not Interested repliers of `camp`.
  // Still lands in the generic "From Campaign Replies" picker, so the source campaign/category
  // can be changed before saving.
  const handleRetarget = (camp) => {
    setEditingCampaign(null);
    setCampaignForm({
      name: `${camp.name} - Retarget`,
      event_type: camp.event_type,
      event_title: '',
      event_date_time: '',
      time_zone: camp.time_zone || userTimeZone,
      is_active: true,
      contacts: [],
      schedules: [
        { timing_type: '1_DAY_BEFORE', custom_minutes_before: '', template: '', media_url: '' }
      ]
    });
    setAudienceMode('campaign_replies');
    setSourceCampaignId(String(camp.id));
    setSourceCategory('NOT_INTERESTED');
    setSourceGroupId('');
    setShowModal(true);
  };

  const handleDelete = async (camp) => {
    if (!window.confirm(`Are you sure you want to delete campaign "${camp.name}"?`)) return;
    try {
      await apiClient.delete(`/reminders/${camp.id}/`);
      addToast('Campaign deleted successfully', 'success');
      fetchCampaigns();
      fetchAnalytics();
    } catch (err) {
      console.error(err);
      addToast(getApiErrorMessage(err, 'Failed to delete campaign'), 'error');
    }
  };

  const handleAddSchedule = () => {
    setCampaignForm({
      ...campaignForm,
      schedules: [
        ...campaignForm.schedules,
        {
          timing_type: '1_HOUR_BEFORE',
          custom_minutes_before: '',
          template: '',
          media_url: ''
        }
      ]
    });
  };

  const handleRemoveSchedule = (idx) => {
    setCampaignForm({
      ...campaignForm,
      schedules: campaignForm.schedules.filter((_, i) => i !== idx)
    });
  };

  const handleScheduleChange = (idx, field, value) => {
    const updatedSchedules = [...campaignForm.schedules];
    const currentSchedule = { ...updatedSchedules[idx], [field]: value };
    
    if (field === 'template') {
      const selectedTpl = templates.find(t => String(t.id) === String(value));
      const autoActions = { ...(currentSchedule.button_actions || {}) };
      if (selectedTpl && Array.isArray(selectedTpl.buttons)) {
        selectedTpl.buttons.forEach(b => {
          const btnText = typeof b === 'string' ? b : (typeof b === 'object' && b.text ? b.text : '');
          if (btnText && !autoActions[btnText]) {
            autoActions[btnText] = { action: 'CONTINUE', reply_text: '' };
          }
        });
      }
      currentSchedule.button_actions = autoActions;

      let count = 0;
      if (selectedTpl) {
        if (selectedTpl.body) {
          const matches = selectedTpl.body.match(/\{\{([^}]+)\}\}/g);
          if (matches && matches.length > 0) {
            const numericIndices = matches
              .map(m => parseInt(m.replace(/\D/g, ''), 10))
              .filter(n => !isNaN(n) && n > 0);
            count = numericIndices.length > 0 ? Math.max(...numericIndices) : matches.length;
          }
        }
        if (!count) {
          if (typeof selectedTpl.variables_count === 'number' && selectedTpl.variables_count > 0) {
            count = selectedTpl.variables_count;
          } else if (Array.isArray(selectedTpl.variables) && selectedTpl.variables.length > 0) {
            count = selectedTpl.variables.length;
          }
        }
      }

      if (count > 0) {
        const existingVars = (currentSchedule.template_variables || {}).body_variables || [];
        const bodyVars = Array.from({ length: count }, (_, i) => existingVars[i] || '');
        currentSchedule.template_variables = {
          ...(currentSchedule.template_variables || {}),
          body_variables: bodyVars
        };
      }
    }

    if (field === 'media_url') {
      const currentVars = currentSchedule.template_variables || {};
      currentSchedule.template_variables = {
        ...currentVars,
        media_url: value
      };
    }

    updatedSchedules[idx] = currentSchedule;
    setCampaignForm({
      ...campaignForm,
      schedules: updatedSchedules
    });
  };

  const handleContactToggle = (contactId) => {
    const isSelected = campaignForm.contacts.includes(contactId);
    const updated = isSelected 
      ? campaignForm.contacts.filter(id => id !== contactId)
      : [...campaignForm.contacts, contactId];
    setCampaignForm({
      ...campaignForm,
      contacts: updated
    });
  };

  const sortedContacts = useMemo(() => {
    const fromTime = contactDateFrom ? new Date(`${contactDateFrom}T00:00:00`).getTime() : null;
    const toTime = contactDateTo ? new Date(`${contactDateTo}T23:59:59`).getTime() : null;

    const filtered = contacts.filter((c) => {
      if (fromTime || toTime) {
        const created = new Date(c.created_at || 0).getTime();
        if (fromTime && created < fromTime) return false;
        if (toTime && created > toTime) return false;
      }
      if (contactSearchQuery) {
        const q = contactSearchQuery.toLowerCase().trim();
        const nameMatch = c.name && c.name.toLowerCase().includes(q);
        const phoneMatch = c.phone_number && c.phone_number.toLowerCase().includes(q);
        if (!nameMatch && !phoneMatch) return false;
      }
      return true;
    });

    return filtered.sort((a, b) => {
      const aTime = new Date(a.created_at || 0).getTime();
      const bTime = new Date(b.created_at || 0).getTime();
      return contactSortOrder === 'oldest' ? aTime - bTime : bTime - aTime;
    });
  }, [contacts, contactSortOrder, contactDateFrom, contactDateTo, contactSearchQuery]);

  const handleSelectAllContacts = () => {
    // Selects/deselects only the currently filtered (date-range) list, not every contact -
    // otherwise the date filter would be pointless for targeting a specific date-added segment.
    const visibleIds = sortedContacts.map(c => c.id);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => campaignForm.contacts.includes(id));
    if (allVisibleSelected) {
      setCampaignForm({ ...campaignForm, contacts: campaignForm.contacts.filter(id => !visibleIds.includes(id)) });
    } else {
      setCampaignForm({ ...campaignForm, contacts: Array.from(new Set([...campaignForm.contacts, ...visibleIds])) });
    }
  };

  return (
    <div style={{ padding: '24px', color: 'var(--text-dark)', display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* Page Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: '28px', fontWeight: '800', color: 'var(--text-dark)' }}>
            Reminder Automation
          </h1>
          <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '4px' }}>
            Schedule and broadcast multi-stage event reminders to webinar, meetings, and appointment attendees.
          </p>
        </div>
        <button
          onClick={handleOpenCreate}
          style={{
            padding: '12px 24px',
            borderRadius: '12px',
            background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
            color: '#fff',
            fontWeight: '600',
            border: 'none',
            cursor: 'pointer',
            boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.2)'
          }}
        >
          Create Campaign
        </button>
      </div>

      {/* Analytics Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
        <div style={{ background: 'var(--surface-white)', borderRadius: '16px', padding: '20px', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ color: 'var(--text-muted)', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Active Reminders</div>
          <div style={{ fontSize: '28px', fontWeight: '800', marginTop: '8px', color: '#2563eb' }}>{analytics.total_scheduled}</div>
        </div>
        <div style={{ background: 'var(--surface-white)', borderRadius: '16px', padding: '20px', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ color: 'var(--text-muted)', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Sent Reminders</div>
          <div style={{ fontSize: '28px', fontWeight: '800', marginTop: '8px', color: '#059669' }}>{analytics.total_sent}</div>
        </div>
        <div style={{ background: 'var(--surface-white)', borderRadius: '16px', padding: '20px', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ color: 'var(--text-muted)', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Delivery Rate</div>
          <div style={{ fontSize: '28px', fontWeight: '800', marginTop: '8px', color: '#e11d48' }}>{analytics.delivery_rate}%</div>
        </div>
        <div style={{ background: 'var(--surface-white)', borderRadius: '16px', padding: '20px', border: '1px solid var(--border-color)', boxShadow: 'var(--shadow-sm)' }}>
          <div style={{ color: 'var(--text-muted)', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase' }}>Attendance Rate</div>
          <div style={{ fontSize: '28px', fontWeight: '800', marginTop: '8px', color: '#d97706' }}>{analytics.attendance_rate}%</div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '8px', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
        <button
          onClick={() => setActiveTab('campaigns')}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            background: activeTab === 'campaigns' ? '#eff6ff' : 'transparent',
            color: activeTab === 'campaigns' ? '#2563eb' : '#64748b',
            border: 'none',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Campaigns
        </button>
        <button
          onClick={() => setActiveTab('history')}
          style={{
            padding: '8px 16px',
            borderRadius: '8px',
            background: activeTab === 'history' ? '#eff6ff' : 'transparent',
            color: activeTab === 'history' ? '#2563eb' : '#64748b',
            border: 'none',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          History & Logs
        </button>
      </div>

      {/* Primary Section */}
      {activeTab === 'campaigns' ? (
        <div style={{ background: 'var(--surface-white)', boxShadow: 'var(--shadow-md)', borderRadius: '16px', border: '1px solid var(--border-color)', overflow: 'hidden' }}>
          {loading ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Loading campaigns...</div>
          ) : campaigns.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>
              No reminder campaigns created yet. Click "Create Campaign" to get started.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ background: 'var(--surface-soft-2)', borderBottom: '1px solid var(--border-color)' }}>
                  <th style={{ padding: '16px', color: 'var(--text-gray)', fontWeight: '600' }}>Campaign Details</th>
                  <th style={{ padding: '16px', color: 'var(--text-gray)', fontWeight: '600' }}>Event Time</th>
                  <th style={{ padding: '16px', color: 'var(--text-gray)', fontWeight: '600' }}>Schedules</th>
                  <th style={{ padding: '16px', color: 'var(--text-gray)', fontWeight: '600' }}>Contacts</th>
                  <th style={{ padding: '16px', color: 'var(--text-gray)', fontWeight: '600' }}>Status</th>
                  <th style={{ padding: '16px', color: 'var(--text-gray)', fontWeight: '600' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((camp) => (
                  <tr key={camp.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '16px' }}>
                      <div style={{ fontWeight: '600' }}>{camp.name}</div>
                      <div style={{ fontSize: '12px', color: '#38bdf8' }}>{camp.event_type} - {camp.event_title}</div>
                    </td>
                    <td style={{ padding: '16px', fontSize: '13px' }}>
                      {new Date(camp.event_date_time).toLocaleString()}
                    </td>
                    <td style={{ padding: '16px' }}>{camp.schedules?.length || 0} stages</td>
                    <td style={{ padding: '16px' }}>{camp.contacts_count || 0} enrolled</td>
                    <td style={{ padding: '16px' }}>
                      <button
                        onClick={() => handleToggleActive(camp)}
                        style={{
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          border: 'none',
                          cursor: 'pointer',
                          background: camp.is_active ? 'rgba(52, 211, 153, 0.15)' : 'rgba(239, 68, 68, 0.15)',
                          color: camp.is_active ? '#34d399' : '#f87171'
                        }}
                      >
                        {camp.is_active ? 'Enabled' : 'Disabled'}
                      </button>
                    </td>
                    <td style={{ padding: '16px' }}>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            const userName = localStorage.getItem('userName') || localStorage.getItem('userEmail') || 'user';
                            // Use the campaign's stable ID, not its name - renaming a campaign after
                            // sharing a name-based link would otherwise break it permanently.
                            const link = `${window.location.origin}/campaign/${encodeURIComponent(userName)}/${camp.id}`;
                            navigator.clipboard.writeText(link);
                            alert(`Public reminder campaign link copied to clipboard!\n\n${link}`);
                          }}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: '4px',
                            background: 'linear-gradient(135deg, #6366f1 0%, #4f46e5 100%)',
                            color: '#fff', border: 'none', padding: '5px 10px',
                            borderRadius: '8px', cursor: 'pointer', fontWeight: '700',
                            fontSize: '11px', boxShadow: '0 2px 6px rgba(99,102,241,0.15)',
                            transition: 'all 0.2s', height: '28px', whiteSpace: 'nowrap'
                          }}
                          title="Copy public reminder campaign registration URL link"
                        >
                          🔗 Public Link
                        </button>
                        <button
                          onClick={() => handleRetarget(camp)}
                          style={{
                            background: 'transparent', border: 'none', color: '#f97316',
                            cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px'
                          }}
                          title="Create a new campaign targeting contacts by how they replied (Interested / Not Interested / etc.)"
                        >
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="6"/><circle cx="12" cy="12" r="2"/></svg>
                        </button>
                        <button onClick={() => handleOpenEdit(camp)} style={{ background: 'transparent', border: 'none', color: '#2563eb', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px' }} title="Edit">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                        </button>
                        <button onClick={() => handleDuplicate(camp)} style={{ background: 'transparent', border: 'none', color: '#7c3aed', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px' }} title="Duplicate">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
                        </button>
                        <button onClick={() => handleDelete(camp)} style={{ background: 'transparent', border: 'none', color: '#e11d48', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: '4px' }} title="Delete">
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div style={{ background: 'var(--surface-white)', boxShadow: 'var(--shadow-md)', borderRadius: '16px', border: '1px solid var(--border-color)', overflow: 'hidden' }}>
          {logs.length === 0 ? (
            <div style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>No reminder executions recorded yet.</div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead>
                <tr style={{ background: 'var(--surface-soft-2)', borderBottom: '1px solid var(--border-color)' }}>
                  <th style={{ padding: '16px', color: 'var(--text-gray)', fontWeight: '600' }}>Contact</th>
                  <th style={{ padding: '16px', color: 'var(--text-gray)', fontWeight: '600' }}>Campaign</th>
                  <th style={{ padding: '16px', color: 'var(--text-gray)', fontWeight: '600' }}>Timing Schedule</th>
                  <th style={{ padding: '16px', color: 'var(--text-gray)', fontWeight: '600' }}>Status</th>
                  <th style={{ padding: '16px', color: 'var(--text-gray)', fontWeight: '600' }}>Scheduled Time</th>
                  <th style={{ padding: '16px', color: 'var(--text-gray)', fontWeight: '600' }}>Sent At</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => (
                  <tr key={log.id} style={{ borderBottom: '1px solid var(--border-color)' }}>
                    <td style={{ padding: '16px' }}>
                      <div style={{ fontWeight: '600' }}>{log.contact_name || 'Unknown'}</div>
                      <div style={{ fontSize: '12px', color: '#94a3b8' }}>{log.contact_phone}</div>
                    </td>
                    <td style={{ padding: '16px' }}>{log.campaign_name}</td>
                    <td style={{ padding: '16px' }}>{log.timing_type}</td>
                    <td style={{ padding: '16px' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        background: log.status === 'SENT' ? 'rgba(52, 211, 153, 0.15)' :
                                    log.status === 'FAILED' ? 'rgba(239, 68, 68, 0.15)' : 'rgba(234, 179, 8, 0.15)',
                        color: log.status === 'SENT' ? '#34d399' :
                               log.status === 'FAILED' ? '#f87171' : '#facc15'
                      }}>
                        {log.status}
                      </span>
                    </td>
                    <td style={{ padding: '16px', fontSize: '13px' }}>
                      {new Date(log.execution_time).toLocaleString()}
                    </td>
                    <td style={{ padding: '16px', fontSize: '13px' }}>
                      {log.sent_at ? new Date(log.sent_at).toLocaleString() : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Creation/Editing Modal */}
      {showModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(15, 23, 42, 0.4)', backdropFilter: 'blur(8px)',
          display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000,
          padding: '24px'
        }}>
          <div style={{
            maxWidth: '750px', width: '100%', maxHeight: '90vh', overflowY: 'auto',
            background: 'var(--surface-white)', borderRadius: '24px', border: '1px solid var(--border-color)',
            padding: '32px', display: 'flex', flexDirection: 'column', gap: '24px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)'
          }}>
            <h2 style={{ fontSize: '22px', fontWeight: '800' }}>
              {editingCampaign ? 'Edit Reminder Campaign' : 'Create Reminder Campaign'}
            </h2>
            
            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#94a3b8' }}>Campaign Name *</label>
                  <input
                    type="text"
                    required
                    value={campaignForm.name}
                    onChange={e => setCampaignForm({ ...campaignForm, name: e.target.value })}
                    placeholder="e.g. Masterclass Event Reminders"
                    style={{
                      padding: '12px', borderRadius: '10px', background: 'var(--surface-soft-2)',
                      border: '1px solid var(--border-color)', color: 'var(--text-dark)', outline: 'none'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#94a3b8' }}>Event Type</label>
                  <select
                    value={campaignForm.event_type}
                    onChange={e => setCampaignForm({ ...campaignForm, event_type: e.target.value })}
                    style={{
                      padding: '12px', borderRadius: '10px', background: 'var(--surface-soft-2)',
                      border: '1px solid var(--border-color)', color: 'var(--text-dark)', outline: 'none'
                    }}
                  >
                    <option value="WEBINAR">Webinar</option>
                    <option value="MEETING">Meeting</option>
                    <option value="EVENT">Event</option>
                    <option value="APPOINTMENT">Appointment</option>
                    <option value="DEMO">Demo Session</option>
                    <option value="CUSTOM">Custom Event</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#94a3b8' }}>Event Title *</label>
                  <input
                    type="text"
                    required
                    value={campaignForm.event_title}
                    onChange={e => setCampaignForm({ ...campaignForm, event_title: e.target.value })}
                    placeholder="e.g. AI Product Launch Showcase"
                    style={{
                      padding: '12px', borderRadius: '10px', background: 'var(--surface-soft-2)',
                      border: '1px solid var(--border-color)', color: 'var(--text-dark)', outline: 'none'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#94a3b8' }}>Time Zone</label>
                  <input
                    type="text"
                    value={campaignForm.time_zone}
                    onChange={e => setCampaignForm({ ...campaignForm, time_zone: e.target.value })}
                    placeholder="UTC, IST, EST"
                    style={{
                      padding: '12px', borderRadius: '10px', background: 'var(--surface-soft-2)',
                      border: '1px solid var(--border-color)', color: 'var(--text-dark)', outline: 'none'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#94a3b8' }}>Event Date & Time *</label>
                  <input
                    type="datetime-local"
                    required
                    value={campaignForm.event_date_time}
                    onChange={e => setCampaignForm({ ...campaignForm, event_date_time: e.target.value })}
                    style={{
                      padding: '12px', borderRadius: '10px', background: 'var(--surface-soft-2)',
                      border: '1px solid var(--border-color)', color: 'var(--text-dark)', outline: 'none'
                    }}
                  />
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '24px' }}>
                  <input
                    type="checkbox"
                    id="camp_active"
                    checked={campaignForm.is_active}
                    onChange={e => setCampaignForm({ ...campaignForm, is_active: e.target.checked })}
                    style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                  />
                  <label htmlFor="camp_active" style={{ fontSize: '14px', fontWeight: '600', cursor: 'pointer' }}>Active (Enable reminders)</label>
                </div>
              </div>

              {/* Target Contacts Selection */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#94a3b8' }}>
                  Target Contacts ({campaignForm.contacts.length} Selected)
                </label>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={() => setAudienceMode('manual')}
                    style={{
                      padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                      border: audienceMode === 'manual' ? '1px solid #3b82f6' : '1px solid var(--border-color)',
                      background: audienceMode === 'manual' ? 'rgba(59, 130, 246, 0.12)' : 'var(--surface-soft-2)',
                      color: audienceMode === 'manual' ? '#3b82f6' : 'var(--text-dark)'
                    }}
                  >
                    Manual Selection
                  </button>
                  <button
                    type="button"
                    onClick={() => setAudienceMode('campaign_replies')}
                    style={{
                      padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                      border: audienceMode === 'campaign_replies' ? '1px solid #f97316' : '1px solid var(--border-color)',
                      background: audienceMode === 'campaign_replies' ? 'rgba(249, 115, 22, 0.12)' : 'var(--surface-soft-2)',
                      color: audienceMode === 'campaign_replies' ? '#f97316' : 'var(--text-dark)'
                    }}
                  >
                    🎯 From Campaign Replies
                  </button>
                  <button
                    type="button"
                    onClick={() => setAudienceMode('contact_group')}
                    style={{
                      padding: '8px 14px', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                      border: audienceMode === 'contact_group' ? '1px solid #10b981' : '1px solid var(--border-color)',
                      background: audienceMode === 'contact_group' ? 'rgba(16, 185, 129, 0.12)' : 'var(--surface-soft-2)',
                      color: audienceMode === 'contact_group' ? '#10b981' : 'var(--text-dark)'
                    }}
                  >
                    👥 From Contact Group
                  </button>
                </div>

                {audienceMode === 'contact_group' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <select
                      value={sourceGroupId}
                      onChange={(e) => handleSourceGroupChange(e.target.value)}
                      style={{
                        padding: '10px', borderRadius: '8px',
                        border: '1px solid var(--border-color)', background: 'var(--surface-soft-2)',
                        color: 'var(--text-dark)', outline: 'none'
                      }}
                    >
                      <option value="">Select contact group...</option>
                      {contactGroups.map((g) => (
                        <option key={g.id} value={g.id}>{g.name} ({g.contact_count ?? 0})</option>
                      ))}
                    </select>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                        {!sourceGroupId
                          ? 'Pick a group above to load its contacts'
                          : `${groupContacts.length} contact${groupContacts.length === 1 ? '' : 's'} in this group`}
                      </span>
                      {groupContacts.length > 0 && (
                        <button
                          type="button"
                          onClick={handleSelectAllGroupContacts}
                          style={{ background: 'transparent', border: 'none', color: '#60a5fa', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                        >
                          {groupContacts.every((c) => campaignForm.contacts.includes(c.id)) ? 'Deselect All' : 'Select All'}
                        </button>
                      )}
                    </div>

                    <div style={{
                      maxHeight: '150px', overflowY: 'auto', background: 'var(--surface-soft-2)',
                      borderRadius: '10px', border: '1px solid var(--border-color)', padding: '12px',
                      display: 'flex', flexDirection: 'column', gap: '8px'
                    }}>
                      {groupContacts.map((c) => (
                        <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input
                            type="checkbox"
                            id={`grp_contact_${c.id}`}
                            checked={campaignForm.contacts.includes(c.id)}
                            onChange={() => handleContactToggle(c.id)}
                            style={{ cursor: 'pointer' }}
                          />
                          <label htmlFor={`grp_contact_${c.id}`} style={{ fontSize: '13px', cursor: 'pointer' }}>
                            {c.name} ({c.phone_number})
                          </label>
                        </div>
                      ))}
                      {sourceGroupId && groupContacts.length === 0 && (
                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>This group has no contacts yet.</span>
                      )}
                    </div>
                  </div>
                ) : audienceMode === 'campaign_replies' ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                      <select
                        value={sourceCampaignId}
                        onChange={(e) => setSourceCampaignId(e.target.value)}
                        style={{
                          flex: '1 1 220px', padding: '10px', borderRadius: '8px',
                          border: '1px solid var(--border-color)', background: 'var(--surface-soft-2)',
                          color: 'var(--text-dark)', outline: 'none'
                        }}
                      >
                        <option value="">Select source campaign...</option>
                        {campaigns.filter((c) => c.id !== editingCampaign?.id).map((c) => (
                          <option key={c.id} value={c.id}>{c.name}</option>
                        ))}
                      </select>
                      <select
                        value={sourceCategory}
                        onChange={(e) => setSourceCategory(e.target.value)}
                        style={{
                          flex: '1 1 200px', padding: '10px', borderRadius: '8px',
                          border: '1px solid var(--border-color)', background: 'var(--surface-soft-2)',
                          color: 'var(--text-dark)', outline: 'none'
                        }}
                      >
                        {REPLY_CATEGORIES.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontSize: '12px', color: '#94a3b8' }}>
                        {!sourceCampaignId
                          ? 'Pick a campaign above to load contacts by how they replied'
                          : loadingSourceContacts
                            ? 'Loading contacts...'
                            : `${sourceContacts.length} contact${sourceContacts.length === 1 ? '' : 's'} matched`}
                      </span>
                      {sourceContacts.length > 0 && (
                        <button
                          type="button"
                          onClick={handleSelectAllSourceContacts}
                          style={{ background: 'transparent', border: 'none', color: '#60a5fa', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                        >
                          {sourceContacts.every((row) => campaignForm.contacts.includes(row.contact_id)) ? 'Deselect All' : 'Select All'}
                        </button>
                      )}
                    </div>

                    <div style={{
                      maxHeight: '150px', overflowY: 'auto', background: 'var(--surface-soft-2)',
                      borderRadius: '10px', border: '1px solid var(--border-color)', padding: '12px',
                      display: 'flex', flexDirection: 'column', gap: '8px'
                    }}>
                      {sourceContacts.map((row) => (
                        <div key={row.contact_id} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input
                            type="checkbox"
                            id={`src_contact_${row.contact_id}`}
                            checked={campaignForm.contacts.includes(row.contact_id)}
                            onChange={() => handleContactToggle(row.contact_id)}
                            style={{ cursor: 'pointer' }}
                          />
                          <label htmlFor={`src_contact_${row.contact_id}`} style={{ fontSize: '13px', cursor: 'pointer' }}>
                            {row.contact_name} ({row.phone_number})
                            <span style={{ color: '#94a3b8', fontSize: '11px' }}> — "{row.button_response}"</span>
                          </label>
                        </div>
                      ))}
                      {sourceCampaignId && !loadingSourceContacts && sourceContacts.length === 0 && (
                        <span style={{ fontSize: '12px', color: '#94a3b8' }}>No contacts matched this reply type for the selected campaign.</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                        <input
                          type="text"
                          placeholder="Search contact..."
                          value={contactSearchQuery}
                          onChange={(e) => setContactSearchQuery(e.target.value)}
                          style={{
                            padding: '5px 10px', borderRadius: '6px', border: '1px solid var(--border-color)',
                            background: 'var(--surface-soft-2)', color: 'var(--text-dark)', fontSize: '12px', outline: 'none',
                            width: '130px'
                          }}
                        />
                        <input
                          type="date"
                          value={contactDateFrom}
                          onChange={(e) => setContactDateFrom(e.target.value)}
                          title="Added from"
                          style={{
                            padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border-color)',
                            background: 'var(--surface-soft-2)', color: 'var(--text-dark)', fontSize: '12px', outline: 'none'
                          }}
                        />
                        <span style={{ fontSize: '11px', color: '#94a3b8' }}>to</span>
                        <input
                          type="date"
                          value={contactDateTo}
                          onChange={(e) => setContactDateTo(e.target.value)}
                          title="Added to"
                          style={{
                            padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border-color)',
                            background: 'var(--surface-soft-2)', color: 'var(--text-dark)', fontSize: '12px', outline: 'none'
                          }}
                        />
                        {(contactDateFrom || contactDateTo) && (
                          <button
                            type="button"
                            onClick={() => { setContactDateFrom(''); setContactDateTo(''); }}
                            style={{ background: 'transparent', border: 'none', color: '#94a3b8', fontSize: '11px', cursor: 'pointer', textDecoration: 'underline' }}
                          >
                            Clear
                          </button>
                        )}
                        <select
                          value={contactSortOrder}
                          onChange={(e) => setContactSortOrder(e.target.value)}
                          style={{
                            padding: '4px 8px', borderRadius: '6px', border: '1px solid var(--border-color)',
                            background: 'var(--surface-soft-2)', color: 'var(--text-dark)', fontSize: '12px', outline: 'none'
                          }}
                        >
                          <option value="latest">Sort: Latest First</option>
                          <option value="oldest">Sort: Oldest First</option>
                        </select>
                      </div>
                      <button
                        type="button"
                        onClick={handleSelectAllContacts}
                        style={{ background: 'transparent', border: 'none', color: '#60a5fa', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}
                      >
                        {sortedContacts.length > 0 && sortedContacts.every(c => campaignForm.contacts.includes(c.id)) ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                    <div style={{
                      maxHeight: '150px', overflowY: 'auto', background: 'var(--surface-soft-2)',
                      borderRadius: '10px', border: '1px solid var(--border-color)', padding: '12px',
                      display: 'flex', flexDirection: 'column', gap: '8px'
                    }}>
                      {sortedContacts.map(c => (
                        <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <input
                            type="checkbox"
                            id={`contact_${c.id}`}
                            checked={campaignForm.contacts.includes(c.id)}
                            onChange={() => handleContactToggle(c.id)}
                            style={{ cursor: 'pointer' }}
                          />
                          <label htmlFor={`contact_${c.id}`} style={{ fontSize: '13px', cursor: 'pointer' }}>
                            {c.name} ({c.phone_number})
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Dynamic Reminder Schedules */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#60a5fa' }}>Broadcast Schedules</h3>
                  <button
                    type="button"
                    onClick={handleAddSchedule}
                    style={{
                      background: '#eff6ff', color: '#2563eb', border: 'none',
                      padding: '6px 12px', borderRadius: '8px', fontWeight: '600', cursor: 'pointer'
                    }}
                  >
                    + Add Reminder
                  </button>
                </div>

                {campaignForm.schedules.map((schedule, idx) => {
                  const selectedTpl = templates.find(t => String(t.id) === String(schedule.template));
                  let bodyVarCount = 0;
                  if (selectedTpl) {
                    if (selectedTpl.body) {
                      const matches = selectedTpl.body.match(/\{\{([^}]+)\}\}/g);
                      if (matches && matches.length > 0) {
                        const numericIndices = matches
                          .map(m => parseInt(m.replace(/\D/g, ''), 10))
                          .filter(n => !isNaN(n) && n > 0);
                        if (numericIndices.length > 0) {
                          bodyVarCount = Math.max(...numericIndices);
                        } else {
                          bodyVarCount = matches.length;
                        }
                      }
                    }
                    if (!bodyVarCount) {
                      if (typeof selectedTpl.variables_count === 'number' && selectedTpl.variables_count > 0) {
                        bodyVarCount = selectedTpl.variables_count;
                      } else if (Array.isArray(selectedTpl.variables) && selectedTpl.variables.length > 0) {
                        bodyVarCount = selectedTpl.variables.length;
                      } else if (selectedTpl.sample_values && typeof selectedTpl.sample_values === 'object') {
                        const keys = Object.keys(selectedTpl.sample_values);
                        if (keys.length > 0) bodyVarCount = keys.length;
                      }
                    }
                  }

                  const configuredBodyVars = (schedule.template_variables || {}).body_variables || [];
                  const activeVarCount = Math.max(bodyVarCount, configuredBodyVars.length);

                  // Extract reply-capable buttons (QUICK_REPLY / FLOW) from the selected template
                  const tplButtons = getTemplateReplyButtons(selectedTpl);

                  return (
                    <div key={idx} style={{
                      background: 'var(--surface-white)', borderRadius: '16px', padding: '20px',
                      border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '16px',
                      position: 'relative'
                    }}>
                      {campaignForm.schedules.length > 1 && (
                        <button
                          type="button"
                          onClick={() => handleRemoveSchedule(idx)}
                          style={{
                            position: 'absolute', top: '16px', right: '16px',
                            background: 'transparent', border: 'none', color: '#f87171',
                            cursor: 'pointer', fontWeight: '600', fontSize: '13px'
                          }}
                        >
                          Remove
                        </button>
                      )}

                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#94a3b8' }}>Reminder {idx + 1}</div>

                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <label style={{ fontSize: '12px', color: '#94a3b8' }}>Timing</label>
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <select
                              value={schedule.timing_type}
                              onChange={e => handleScheduleChange(idx, 'timing_type', e.target.value)}
                              style={{
                                flex: 1, padding: '10px', borderRadius: '8px',
                                background: 'var(--surface-soft-2)', border: '1px solid var(--border-color)',
                                color: 'var(--text-dark)', outline: 'none'
                              }}
                            >
                              <option value="AT_EVENT_TIME">At Event Date & Time (Exact)</option>
                              <option value="SPECIFIC_DATETIME">Custom Date & Time</option>
                              <option value="7_DAYS_BEFORE">7 Days Before</option>
                              <option value="5_DAYS_BEFORE">5 Days Before</option>
                              <option value="3_DAYS_BEFORE">3 Days Before</option>
                              <option value="2_DAYS_BEFORE">2 Days Before</option>
                              <option value="1_DAY_BEFORE">1 Day Before</option>
                              <option value="6_HOURS_BEFORE">6 Hours Before</option>
                              <option value="1_HOUR_BEFORE">1 Hour Before</option>
                              <option value="CUSTOM">Custom Minutes Before</option>
                            </select>
                            {schedule.timing_type === 'CUSTOM' && (
                              <input
                                type="number"
                                required
                                placeholder="Minutes"
                                value={schedule.custom_minutes_before}
                                onChange={e => handleScheduleChange(idx, 'custom_minutes_before', e.target.value)}
                                style={{
                                  width: '100px', padding: '10px', borderRadius: '8px',
                                  background: 'var(--surface-soft-2)', border: '1px solid var(--border-color)',
                                  color: 'var(--text-dark)', outline: 'none'
                                }}
                              />
                            )}
                            {schedule.timing_type === 'SPECIFIC_DATETIME' && (
                              <input
                                type="datetime-local"
                                required
                                value={(schedule.template_variables || {}).scheduled_datetime || ''}
                                onChange={e => handleScheduleVariablesChange(idx, 'scheduled_datetime', e.target.value)}
                                style={{
                                  padding: '10px', borderRadius: '8px',
                                  background: 'var(--surface-soft-2)', border: '1px solid var(--border-color)',
                                  color: 'var(--text-dark)', outline: 'none', fontSize: '12px'
                                }}
                              />
                            )}
                          </div>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                          <label style={{ fontSize: '12px', color: '#94a3b8' }}>Message Template *</label>
                          <select
                            value={schedule.template}
                            onChange={e => handleScheduleChange(idx, 'template', e.target.value)}
                            style={{
                              padding: '10px', borderRadius: '8px',
                              background: 'var(--surface-soft-2)', border: '1px solid var(--border-color)',
                              color: 'var(--text-dark)', outline: 'none'
                            }}
                          >
                            <option value="">Select Template</option>
                            {templates.map(t => (
                              <option key={t.id} value={t.id}>{t.name}</option>
                            ))}
                          </select>
                        </div>
                      </div>

                      {/* Template Variable Inputs */}
                      {selectedTpl && (
                        <div style={{ background: 'var(--surface-soft-2)', borderRadius: '12px', padding: '14px', border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--brand-purple)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 20h9"></path><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"></path></svg>
                              Template Variable Inputs for "{selectedTpl.name}"
                            </div>
                            <button
                              type="button"
                              onClick={() => {
                                const currentVars = (schedule.template_variables || {}).body_variables || [];
                                handleScheduleBodyVariableChange(idx, currentVars.length, '');
                              }}
                              style={{ background: 'rgba(124, 77, 255, 0.1)', color: 'var(--brand-purple)', border: 'none', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                            >
                              + Add Variable Field
                            </button>
                          </div>

                          {/* Template Body Preview */}
                          {selectedTpl.body && (
                            <div style={{ fontSize: '12px', background: 'var(--surface-white)', padding: '10px 12px', borderRadius: '8px', border: '1px solid var(--border-color)', color: '#334155' }}>
                              <div style={{ fontWeight: '700', color: 'var(--brand-purple)', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px' }}>
                                Template Text Preview
                              </div>
                              <div style={{ fontStyle: 'italic', color: '#475569', lineHeight: '1.4' }}>
                                "{selectedTpl.body}"
                              </div>
                            </div>
                          )}

                          {['IMAGE', 'VIDEO', 'DOCUMENT'].includes(selectedTpl.header_type) && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>
                                  Header {selectedTpl.header_type} Media URL
                                  <span style={{ fontSize: '10px', fontWeight: '400', color: '#94a3b8', marginLeft: '6px' }}>(Optional)</span>
                                </label>
                                {selectedTpl.header_type === 'VIDEO' && (
                                  <span style={{
                                    fontSize: '10px', background: 'rgba(34, 197, 94, 0.12)', color: '#16a34a',
                                    border: '1px solid rgba(34, 197, 94, 0.25)', borderRadius: '20px',
                                    padding: '2px 8px', fontWeight: '600'
                                  }}>
                                    ✓ Auto: uses your uploaded Meta video
                                  </span>
                                )}
                              </div>
                              <input
                                type="text"
                                placeholder={
                                  selectedTpl.header_type === 'VIDEO'
                                    ? 'Leave blank to use your Meta-uploaded video automatically'
                                    : `https://example.com/media.${selectedTpl.header_type.toLowerCase() === 'image' ? 'jpg' : 'pdf'}`
                                }
                                value={schedule.media_url || (schedule.template_variables || {}).media_url || ''}
                                onChange={e => handleScheduleChange(idx, 'media_url', e.target.value)}
                                style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-color)', fontSize: '13px', outline: 'none', background: 'var(--surface-white)' }}
                              />
                              {selectedTpl.header_type === 'VIDEO' && !(schedule.media_url || (schedule.template_variables || {}).media_url) && (
                                <div style={{ fontSize: '11px', color: '#64748b', padding: '6px 10px', background: 'rgba(124, 77, 255, 0.06)', borderRadius: '6px', border: '1px solid rgba(124, 77, 255, 0.15)' }}>
                                  💡 The backend will automatically fetch the video you uploaded when creating this template on Meta. No URL needed.
                                </div>
                              )}
                            </div>
                          )}

                          {selectedTpl.header_type === 'TEXT' && (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                              <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>
                                Header Text Parameter Value
                              </label>
                              <input
                                type="text"
                                placeholder="Value for Header Variable"
                                value={(schedule.template_variables || {}).header_text || ''}
                                onChange={e => handleScheduleVariablesChange(idx, 'header_text', e.target.value)}
                                style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-color)', fontSize: '13px', outline: 'none', background: 'var(--surface-white)' }}
                              />
                            </div>
                          )}

                          {activeVarCount > 0 ? (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                              <label style={{ fontSize: '12px', fontWeight: '600', color: '#64748b' }}>
                                Body Variables ({activeVarCount} parameter{activeVarCount > 1 ? 's' : ''} configured)
                              </label>
                              {Array.from({ length: activeVarCount }).map((_, vIdx) => {
                                let varHint = '';
                                if (Array.isArray(selectedTpl.variables) && selectedTpl.variables[vIdx]) {
                                  varHint = selectedTpl.variables[vIdx];
                                } else if (selectedTpl.body) {
                                  const matches = selectedTpl.body.match(/\{\{([^}]+)\}\}/g);
                                  if (matches && matches[vIdx]) {
                                    const token = matches[vIdx].replace(/[\{\}]/g, '').trim();
                                    if (isNaN(token)) varHint = token;
                                  }
                                }
                                if (!varHint && selectedTpl.sample_values) {
                                  if (Array.isArray(selectedTpl.sample_values) && selectedTpl.sample_values[vIdx]) {
                                    varHint = `e.g. ${selectedTpl.sample_values[vIdx]}`;
                                  } else if (typeof selectedTpl.sample_values === 'object') {
                                    const s = selectedTpl.sample_values[String(vIdx + 1)] || selectedTpl.sample_values[vIdx];
                                    if (s) varHint = `e.g. ${s}`;
                                  }
                                }

                                return (
                                  <div key={vIdx} style={{ display: 'flex', flexDirection: 'column', gap: '6px', background: 'var(--surface-white)', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                        <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--brand-purple)' }}>
                                          Variable &#123;&#123;{vIdx + 1}&#125;&#125; {varHint && <span style={{ color: '#64748b', fontWeight: '600', marginLeft: '4px' }}>({varHint})</span>}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => handleRemoveScheduleBodyVariable(idx, vIdx)}
                                          title="Delete this variable field"
                                          style={{ background: 'rgba(248, 113, 113, 0.1)', color: '#ef4444', border: 'none', borderRadius: '4px', padding: '2px 6px', fontSize: '11px', cursor: 'pointer', fontWeight: '700' }}
                                        >
                                          ✕ Remove
                                        </button>
                                      </div>
                                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                                        <button type="button" onClick={() => handleInsertVariableShortcut(idx, vIdx, '{{contact.name}}')} style={{ fontSize: '11px', background: 'rgba(124, 77, 255, 0.1)', color: 'var(--brand-purple)', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontWeight: '600' }}>+ Contact Name</button>
                                        <button type="button" onClick={() => handleInsertVariableShortcut(idx, vIdx, campaignForm.event_title || 'Webinar')} style={{ fontSize: '11px', background: 'rgba(124, 77, 255, 0.1)', color: 'var(--brand-purple)', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontWeight: '600' }}>+ Event Title</button>
                                        <button type="button" onClick={() => handleInsertVariableShortcut(idx, vIdx, campaignForm.event_date_time || 'Upcoming Date')} style={{ fontSize: '11px', background: 'rgba(124, 77, 255, 0.1)', color: 'var(--brand-purple)', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontWeight: '600' }}>+ Event Date</button>
                                        <button type="button" onClick={() => handleInsertVariableShortcut(idx, vIdx, '{{contact.phone}}')} style={{ fontSize: '11px', background: 'rgba(124, 77, 255, 0.1)', color: 'var(--brand-purple)', border: 'none', borderRadius: '4px', padding: '2px 8px', cursor: 'pointer', fontWeight: '600' }}>+ Phone</button>
                                      </div>
                                    </div>
                                    <input
                                      type="text"
                                      placeholder={varHint ? `Value for ${varHint}` : `Value for {{${vIdx + 1}}} (e.g. John Doe, Webinar Title, or {{contact.name}})`}
                                      value={((schedule.template_variables || {}).body_variables || [])[vIdx] || ''}
                                      onChange={e => handleScheduleBodyVariableChange(idx, vIdx, e.target.value)}
                                      style={{ padding: '8px 12px', borderRadius: '8px', border: '1px solid var(--border-color)', fontSize: '13px', outline: 'none', background: 'var(--surface-soft-2)' }}
                                    />
                                  </div>
                                );
                              })}
                            </div>
                          ) : (
                            <div style={{ fontSize: '12px', color: '#64748b', fontStyle: 'italic', background: 'var(--surface-white)', padding: '10px', borderRadius: '8px', border: '1px solid var(--border-color)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span>No body variables auto-detected in template text. Click button to add inputs if required.</span>
                              <button
                                type="button"
                                onClick={() => handleScheduleBodyVariableChange(idx, 0, '')}
                                style={{ background: 'rgba(124, 77, 255, 0.1)', color: 'var(--brand-purple)', border: 'none', padding: '4px 8px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                              >
                                + Add Variable Field
                              </button>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Tile-Level Dynamic Button Reply Follow-Up Actions Panel - fully data-driven, */}
                      {/* recurses into whichever templates/buttons are actually configured, no hardcoding. */}
                      <div style={{ background: 'var(--surface-soft-2)', borderRadius: '12px', padding: '14px', border: '1px solid var(--border-color)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--brand-purple)', display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"></path></svg>
                          Button Reply Follow-up Actions
                        </div>

                        <ButtonActionsEditor
                          buttons={tplButtons}
                          actions={schedule.button_actions || {}}
                          onChange={(newActions) => handleScheduleChange(idx, 'button_actions', newActions)}
                          templates={templates}
                          followUpWorkflows={followUpWorkflows}
                          campaignForm={campaignForm}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px' }}>
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  style={{
                    padding: '10px 20px', borderRadius: '10px', background: 'var(--surface-soft)',
                    color: 'var(--text-gray)', border: 'none', cursor: 'pointer', fontWeight: '600'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  style={{
                    padding: '10px 24px', borderRadius: '10px',
                    background: 'linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%)',
                    color: 'var(--text-dark)', border: 'none', cursor: 'pointer', fontWeight: '600'
                  }}
                >
                  {saving ? 'Saving...' : 'Save Campaign'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default ReminderAutomation;
